// src/components/TermsAndConditions.js

import React from 'react';
import { Helmet } from 'react-helmet';
import Header from './Header';
import Footer from './Footer';

const TermsAndConditions = ({ propertyDetails, pageNumber }) => {
  return (
    <>
      <Helmet>
        <title>Terms and Conditions - Houval</title>
      </Helmet>
      <div className={`bg-gray-200 w-[794px] h-[1123px] flex flex-col page-break`}>
        {/* Header Section */}
        <Header pageNumber={pageNumber} propertyDetails={propertyDetails} />

        {/* Main Content */}
        <div className="flex-grow p-8">
          <h2 className="text-2xl font-semibold mb-6">Terms and Conditions</h2>

          {/* Introduction */}
          <section className="mb-6">
            <h3 className="text-xl font-semibold mb-2">1. Introduction</h3>
            <p className="text-sm leading-relaxed text-gray-700 mb-4">
              These Terms and Conditions [“T&C”] govern the use of our property valuation estimate services provided through Houval. By utilizing our application, you agree to comply with and be bound by these terms. Please read them carefully before proceeding.
            </p>
          </section>

          {/* Scope of Valuation */}
          <section className="mb-6">
            <h3 className="text-xl font-semibold mb-2">2. Scope of Valuation Estimate</h3>
            <p className="text-sm leading-relaxed text-gray-700 mb-4">
              Houval provides a rebuild cost estimation and automatically forecasts tax depreciation schedules based on the rebuild estimates. The valuation estimates are derived from the data you provide and current market conditions. The estimation considers various factors, including but not limited to:
            </p>
            <ul className="list-disc list-inside text-sm text-gray-700 mb-4">
              <li>Current market prices and trends.</li>
              <li>Labor and material costs.</li>
              <li>Property specifications and features.</li>
              <li>Geographical location and its impact on value.</li>
              <li>Data from external financial models, such as Rawlinsons.</li>
            </ul>
          </section>

          {/* User Responsibilities */}
          <section className="mb-6">
            <h3 className="text-xl font-semibold mb-2">3. User Responsibilities</h3>
            <p className="text-sm leading-relaxed text-gray-700 mb-4">
              Users are responsible for providing accurate and comprehensive data during the property scan. Incomplete or inaccurate data may lead to erroneous valuation estimates. It is imperative to ensure that all property components are thoroughly scanned and documented to facilitate precise estimation outcomes.
            </p>
          </section>

          {/* Limitations and Disclaimers */}
          <section className="mb-6">
            <h3 className="text-xl font-semibold mb-2">4. Limitations and Disclaimers</h3>
            <p className="text-sm leading-relaxed text-gray-700 mb-4">
              While we strive to provide accurate and reliable valuation estimates, certain limitations apply:
            </p>
            <ul className="list-disc list-inside text-sm text-gray-700 mb-4">
              <li>
                <strong>Market Fluctuations:</strong> Property values are subject to change based on market dynamics. Our valuation estimates reflect current market conditions and may not account for future shifts.
              </li>
              <li>
                <strong>Labor and Material Costs:</strong> Houval uses current labor and material costs to estimate valuations. Changes in these costs due to supply chain and labour market conditions may affect estimation accuracy.
              </li>
              <li>
                <strong>AI Model Limitations:</strong> While our AI models assist in analyzing property data, they may not accurately determine complex features or hidden aspects of a property. Human oversight is recommended for comprehensive evaluations.
              </li>
              <li>
                <strong>Incomplete Scans:</strong> Partial scans or omitted property components can result in incomplete valuation estimates. Users must ensure thorough data collection.
              </li>
              <li>
                <strong>External Financial Models:</strong> Houval utilizes data from external financial models, such as Rawlinsons. The accuracy of our valuation estimates is dependent on the accuracy of these external models, which we do not control.
              </li>
              <li>
                <strong>No Warranty:</strong> We do not warrant the accuracy, completeness, or reliability of the valuation estimates provided. The service is offered "as is" without any express or implied warranties.
              </li>
              <li>
                <strong>Valuation Estimate:</strong> Houval provides a valuation estimate and not a certified valuation. It should not be solely relied upon for making financial or investment decisions.
              </li>
            </ul>
          </section>
        </div>

        {/* Footer Section */}
        <Footer />
      </div>
      <div className={`bg-gray-200 w-[794px] h-[1123px] flex flex-col page-break`}>
        {/* Header Section */}
        <Header pageNumber={pageNumber + 1} propertyDetails={propertyDetails} />

        {/* Main Content */}
        <div className="flex-grow p-8">

          {/* Accuracy of Data */}
          <section className="mb-6">
            <h3 className="text-xl font-semibold mb-2">5. Accuracy of Data</h3>
            <p className="text-sm leading-relaxed text-gray-700 mb-4">
              The accuracy of the valuation estimate is highly dependent on the quality and completeness of the data input by the user. Users must verify the information provided to ensure that valuation estimates are reflective of the property's true condition and attributes.
            </p>
          </section>
          
          {/* Confidentiality */}
          <section className="mb-6">
            <h3 className="text-xl font-semibold mb-2">6. Confidentiality</h3>
            <p className="text-sm leading-relaxed text-gray-700 mb-4">
              All property data provided by users is treated with strict confidentiality. We employ robust security measures to protect your information from unauthorized access, disclosure, alteration, or destruction.
            </p>
          </section>

          {/* Liability */}
          <section className="mb-6">
            <h3 className="text-xl font-semibold mb-2">7. Liability</h3>
            <p className="text-sm leading-relaxed text-gray-700 mb-4">
              Under no circumstances shall Propertise AI Group Pty Ltd, its affiliates, or their licensors, service providers, employees, agents, officers, or directors be liable for any direct, indirect, incidental, consequential, or punitive damages arising out of or in connection with your use, or inability to use, Houval or the valuation estimates provided. This includes, but is not limited to, personal injury, emotional distress, loss of revenue, loss of profits, or any other damages, whether foreseeable or not.
            </p>
            <p className="text-sm leading-relaxed text-gray-700 mb-4">
              You agree to indemnify and hold us harmless from any claims, damages, or expenses (including legal fees) arising out of or related to your use of Houval, your violation of these T&Cs, or your infringement of any intellectual property or other rights of any person or entity.
            </p>
            <p className="text-sm leading-relaxed text-gray-700 mb-4">
              Nothing in these T&Cs affects any liability which cannot be excluded or limited under applicable law. This includes liability for death or personal injury caused by our negligence or the negligence of our employees, agents, or subcontractors and for fraud or fraudulent misrepresentation.
            </p>
          </section>

          {/* Governing Law */}
          <section className="mb-6">
            <h3 className="text-xl font-semibold mb-2">8. Governing Law</h3>
            <p className="text-sm leading-relaxed text-gray-700 mb-4">
              These T&Cs are governed by and construed in accordance with the laws of Western Australia. Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts in Western Australia.
            </p>
          </section>

          {/* Acceptance of Terms */}
          <section className="mb-6">
            <h3 className="text-xl font-semibold mb-2">9. Acceptance of Terms</h3>
            <p className="text-sm leading-relaxed text-gray-700 mb-4">
              By using our valuation estimate services, you acknowledge that you have read, understood, and agree to be bound by these T&Cs. If you do not agree with any part of these terms, you must refrain from using the application.
            </p>
          </section>

          {/* Contact Information */}
          <section className="mb-6">
            <h3 className="text-xl font-semibold mb-2">10. Contact Information</h3>
            <p className="text-sm leading-relaxed text-gray-700 mb-4">
              If you have any questions or concerns regarding these T&Cs or our valuation estimate services, please contact us at:
            </p>
            <ul className="list-disc list-inside text-sm text-gray-700">
              <li><strong>Email:</strong> <a href="mailto:info@propertiseaigroup.com.au" className="text-blue-600 underline">info@propertiseaigroup.com.au</a></li>
              <li><strong>Address:</strong> 402/2 Goatcher Vista, Shenton Park, WA 6008</li>
            </ul>
          </section>
        </div>

        {/* Footer Section */}
        <Footer />
      </div>
    </>
  );
};

export default TermsAndConditions;
